import Bugsnag from '@bugsnag/js';
import type { ErrorInfo } from 'react';
import { version } from '../../package.json';

export function initBugsnag() {
  // Initialize Bugsnag once at app startup
  Bugsnag.start({
    apiKey: import.meta.env.VITE_BUGSNAG_KEY,
    enabledReleaseStages: ['production', 'staging'],
    releaseStage: import.meta.env.MODE,
    appVersion: version,
    plugins: [],
  });

  // If we want to track performance later on, add here
  // Initialize performance monitoring (optional but useful)
  // BugsnagPerformance.start({
  //   apiKey: import.meta.env.VITE_BUGSNAG_KEY,
  //   releaseStage: import.meta.env.MODE,
  // });
}

export const reportError = (error: unknown, info: ErrorInfo) => {
  const errorToReport = error instanceof Error ? error : new Error(String(error));
  Bugsnag.notify(errorToReport, (event) => {
    if (info?.componentStack) {
      event.addMetadata('react', { componentStack: info.componentStack });
    }
  });
};

/** Options for {@link notifyError}. */
export interface NotifyErrorOptions {
  /** A short, grouping-friendly label (e.g. `zones:sync:create`). */
  context?: string;
  /** Extra structured context attached under the "details" tab. */
  metadata?: Record<string, unknown>;
}

/**
 * Report an unexpected runtime failure (a rejected mutation, a dead SSE
 * stream, an infra 5xx) to Bugsnag from outside a React error boundary.
 * Use this for failures the user cannot act on — NOT for expected,
 * user-actionable outcomes (validation, 403/404) that already surface as a
 * toast. Non-Error inputs are coerced so a thrown string still reports.
 */
export const notifyError = (error: unknown, options: NotifyErrorOptions = {}): void => {
  const errorToReport = error instanceof Error ? error : new Error(String(error));
  Bugsnag.notify(errorToReport, (event) => {
    if (options.context) {
      event.context = options.context;
    }
    if (options.metadata) {
      event.addMetadata('details', options.metadata);
    }
  });
};
